import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { change } from 'redux-form';
import { addWhdStatement, createWhdStatement, getStatementVersions } from 'actions';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import WithholdingStatementUpload from 'components/Common/WithholdingStatementUpload';
import StatementTypes from 'common/Forms/StatementTypes';
import ConfirmModal from 'components/Modals/ConfirmModal';
import moment from 'moment';
import valueOrEmpty from 'common/App/valueOrEmpty';
import WhdStatementVersions from './WhdStatementVersions';

const sanitizeAiJobStatus = (status) => {
  if (status == null) return '';
  try {
    // Convert to string and strip any HTML tags
    return String(status).replace(/<[^>]*>/g, '').trim();
  } catch (e) {
    return '';
  }
};

const UploadWithholdingSection = ({ accountId, topAccountId, topAccountExpectedWhs, formId, isAddingStatement, withholdingStatement, isFormDirty, assignedUser, whdValidationResult, aiJobExists, aiJobStatus, formRequiresWhs }) => {
  const [whdStatement, setWhdStatement] = useState(null);
  const [createWhdStatementType, setCreateWhdStatementType] = useState('');
  const [addWhdStatementType, setAddWhdStatementType] = useState('');
  const [modalStatementType, setModalStatementType] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [showNewWhdModal, setShowNewWhdModal] = useState(false);
  const [newUploadType, setNewUploadType] = useState(null);
  const isCreatingNewWhd = useSelector(state => state.forms.isCreatingNewWhd);
  const permissions = useSelector(state => state.auth.permissions);
  const line1Name = useSelector(state => state?.form && state.form['w8imy-form']?.values?.part1?.entityName?.name1);
  const showAiWithholdingStatements = useSelector(state => state.auth.settings?.showAiWithholdingStatements);

  const canUploadStmtBos = AccessControl.hasPermission(permissions, [ActivityPerms.scannedWhdStmtBoUpload], true);
  const canViewDigitalWhs = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrViewWhs], true);
  const canCreateDigitalWhs = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrCreateWhs], true);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getStatementVersions(accountId, 1));
  }, []);

  function onWhdStatementFileUploadError(file, response) {
    setWhdStatement(null);
    setDisabled(true);
  }

  function onWhdStatementFileUploadSuccess(file, response) {
    setWhdStatement(response);
  }

  const onCreateWhdStatementTypeChange = (e, newValue) => {
    setCreateWhdStatementType(newValue);
    setNewUploadType('Create');
    setAddWhdStatementType('');
  };

  const onAddWhdStatementTypeChange = (e, newValue) => {
    setAddWhdStatementType(newValue);
    setNewUploadType('Add');
    setCreateWhdStatementType('');
    setWhdStatement(null);
  };

  const navigateToWithholdingStatement = () => {
    dispatch(push(`/whd-statement/${ topAccountId }`));
  };

  function onFileUploadCancel(file) {
    toastr.info('', `Upload of ${ file.name } has been canceled`);
  }

  function onDeleteWhdStatement(file) {
    if (file != null) {
      setWhdStatement(null);
      dispatch(change('uploadingPdfForm', 'benefFormType', null));
      setDisabled(true);
    }
  }

  function submitWithholdingStatement() {
    const data = {
      formId,
      whdStatementType: newUploadType === 'Add' ? addWhdStatementType : createWhdStatementType,
      whdStatement,
      statementFormat: whdStatement?.statementFormat
    };
    dispatch(addWhdStatement(accountId, data));
  }

  const displayCreateWhdModal = () => {
    if (isFormDirty) {
      toastr.warning('', 'The form has changes that are not saved. Please Validate the form before creating the statement');
      return;
    }

    if (!line1Name || line1Name.trim().length === 0) {
      toastr.warning('', 'Line 1 must be entered before a withholding statement can be created');
      return;
    }

    const selectedStatement = document.getElementById('statementType');
    const selectedStatementText = selectedStatement.options[selectedStatement.selectedIndex].text;
    const statement = line1Name && line1Name.length > 0 ? `${ selectedStatementText } for ${ line1Name }` : selectedStatementText;
    setModalStatementType(statement);
    setShowNewWhdModal(true);
  };

  const confirmCreateNewStatement = () => {
    setShowNewWhdModal(false);
    if (!line1Name || line1Name.trim() === '') {
      toastr.error('', 'Line 1 must be entered before a withholding statement can be created');
      return;
    }
    dispatch(createWhdStatement(formId, accountId, createWhdStatementType));
  };
  const getDate = date => (date ? moment(date).format('MMM DD, YYYY') : valueOrEmpty(date));
  const toggleDescription = (element) => {
    if (element.className.indexOf(' active') === -1) {
      element.className += ' active';
    } else {
      element.className = element.className.replace(' active', '');
    }
  };

  const isStructure = !!topAccountId;
  const isTopAccount = isStructure && accountId === topAccountId;
  const showAiJobStatusOnly = !withholdingStatement && aiJobExists && showAiWithholdingStatements;

  return (
    <div>
      <h1 className="mb-0">Withholding Statement</h1>
      <div className="upload-withholding-section-content">
        { (!isStructure || isTopAccount) && !formRequiresWhs && <div>
          <p>Based on the elections on this W-8IMY, a Withholding Statement is not required, and new statements cannot be added.</p>
          <p>If any published Withholding Statements are linked to this account, they will appear below.</p>
        </div> }
        <div>
          { (!isTopAccount || formRequiresWhs) && withholdingStatement && canViewDigitalWhs && <React.Fragment>
            <h2 className="upload-status">{ StatementTypes.getDescription(withholdingStatement.type) } Uploaded</h2>
            <div className="whd-statement-draft-content">
              <div className="columns pb-2 m-0">
                <div className="col-9">
                  <div><b>Name: {withholdingStatement?.statementName ?? 'N/A'}</b> (Draft)</div>
                  <div>Effective: {getDate(withholdingStatement?.effectiveDate)} {withholdingStatement?.effectiveTo && `- ${ getDate(withholdingStatement.effectiveTo) }`}
                  </div>
                  <div>Validity: {whdValidationResult?.valid ? <span className="valid-text">Valid</span> : <span className="invalid-text">Invalid</span>}</div>
                  <div aria-hidden="true" onClick={ e => toggleDescription(e.target) }>
                    <span className="text-ellipsis text-expander">Description: {withholdingStatement?.statementDescription ?? 'N/A'}</span>
                  </div>
                  <div>User: {assignedUser}</div>
                </div>
                <div className="clearfix col-3">
                  <button id="open-whd-statement" className="btn btn-primary float-right" type="button" onClick={ () => navigateToWithholdingStatement() }>Open Draft</button>
                </div>
              </div>
            </div>
          </React.Fragment> }
          <WhdStatementVersions accountId={ accountId } getVersionsCallback={ (id, pageNumber) => dispatch(getStatementVersions(id, pageNumber)) } formRequiresWhs={ formRequiresWhs } />
          { !canViewDigitalWhs && <h4>Insufficient permissions to view digital withholding statement.</h4>}
        </div>
        { ((isStructure && !isTopAccount) || formRequiresWhs) && !withholdingStatement && <div className="create-new-whd-group">
          { showAiJobStatusOnly && <p>{ sanitizeAiJobStatus(aiJobStatus) }</p> }
          { !showAiJobStatusOnly && <React.Fragment>
            { canCreateDigitalWhs && <React.Fragment>
              <p>Please use the functionality below to either create or upload a withholding statement.</p>
              <h2>Create An Empty Withholding Statement</h2>
              <div className="form-group">
                <select id="statementType" className="form-select" value={ createWhdStatementType } onChange={ e => onCreateWhdStatementTypeChange(e, e.target.value) }>
                  <option value="" disabled>Choose a statement type</option>
                  <option value="altwhdstatement">Alt Statement</option>
                  <option value="detailedwhdstatement">Detailed Statement</option>
                  <option value="simplewhdstatement">Simple Statement</option>
                  <option value="pooledwhdstatement">Pooled Statement</option>
                </select>
              </div>
              <div className="form-group text-right">
                <button
                  id="create-whd-statement"
                  className="btn btn-primary btn-submit"
                  type="button"
                  disabled={ createWhdStatementType === null || createWhdStatementType.length <= 0 || isCreatingNewWhd || isAddingStatement || newUploadType !== 'Create' }
                  onClick={ () => displayCreateWhdModal() }
                >
                  Create Statement
                </button>
              </div>
            </React.Fragment> }
            { !canCreateDigitalWhs && <h4>Insufficient permissions to create a new digital withholding statement.</h4> }
          </React.Fragment> }
        </div> }
        { ((isStructure && !isTopAccount) || formRequiresWhs) && !withholdingStatement && !showAiJobStatusOnly && <div className="create-new-whd-group">
          <h2>Upload a Withholding Statement</h2>
          { canUploadStmtBos && <div>
            <WithholdingStatementUpload
              accountId={ accountId }
              statementType={ addWhdStatementType }
              formErrors={ null }
              onStatementTypeChange={ (e, newValue) => onAddWhdStatementTypeChange(e, newValue) }
              onFileUploadSuccess={ (file, response) => onWhdStatementFileUploadSuccess(file, response) }
              onFileUploadError={ (file, error) => onWhdStatementFileUploadError(file, error) }
              onFileUploadCancel={ file => onFileUploadCancel(file) }
              onFileDeleteSuccess={ file => onDeleteWhdStatement(file) }
              disableConfirm={ e => setDisabled(e) }
              ddLabelText=""
              singleColumnLayout={ !canUploadStmtBos }
            />
            <div className="text-right">
              <button
                id="add-whd-statement"
                className="btn btn-primary btn-submit"
                onClick={ () => submitWithholdingStatement() }
                type="button"
                disabled={ disabled || addWhdStatementType === null || addWhdStatementType.length <= 0 || isAddingStatement || newUploadType !== 'Add' }
              >
                Upload Statement
              </button>
            </div>
          </div> }
          { !canUploadStmtBos && <h4>Insufficient permissions to upload a withholding statement and underlying documentation</h4> }
        </div> }
      </div>
      { showNewWhdModal && <ConfirmModal
        key="createNewWhdStatement"
        isShown={ showNewWhdModal }
        onConfirm={ () => confirmCreateNewStatement() }
        onCancel={ () => setShowNewWhdModal(false) }
        title="Confirm Creation of New Withholding Statement"
        message={ `Are you sure you want to create a ${ modalStatementType }?` }
      /> }
    </div>
  );
};

UploadWithholdingSection.propTypes = {
  accountId: PropTypes.number.isRequired,
  topAccountId: PropTypes.number,
  topAccountExpectedWhs: PropTypes.number,
  formId: PropTypes.number.isRequired,
  isAddingStatement: PropTypes.bool.isRequired,
  withholdingStatement: PropTypes.shape(
    { type: PropTypes.number,
      statementName: PropTypes.string,
      statementDescription: PropTypes.string,
      effectiveDate: PropTypes.string,
      effectiveTo: PropTypes.string
    }
  ),
  isFormDirty: PropTypes.bool,
  assignedUser: PropTypes.string,
  whdValidationResult: PropTypes.shape(),
  aiJobExists: PropTypes.bool,
  aiJobStatus: PropTypes.string,
  formRequiresWhs: PropTypes.bool
};

UploadWithholdingSection.defaultProps = {
  topAccountId: null,
  topAccountExpectedWhs: null,
  withholdingStatement: null,
  isFormDirty: false,
  assignedUser: '',
  whdValidationResult: null,
  aiJobExists: false,
  aiJobStatus: '',
  formRequiresWhs: false
};

export default UploadWithholdingSection;
